import { ScenarioEvent } from './ScenarioEvent';

export interface FireScenarioInit {
  id: string;
  name: string;
  monthlyExpenses: number;
  currentPortfolio?: number;
  annualReturn?: number;
  inflation?: number;
  currentAge: number;
  retirementAge: number;
  monthlyContribution?: number;
  withdrawalTiming?:
    | 'beginningOfYear'
    | 'twicePerYear'
    | 'endOfYear';
  swr?: number;
  contributionGrowthEnabled?: boolean;
  events?: readonly (ScenarioEvent | import('./ScenarioEvent').ScenarioEventInit)[];
  phase?: 'mvp' | 'area2' | 'area3' | 'timing';
  createdAt?: Date;
  updatedAt?: Date;
}

import {
  CalculationMode,
  FireMethod,
  WithdrawalTiming,
  ScenarioPhase,
} from '../enums';
import { MoneyAmount } from '../value-objects/MoneyAmount';
import { PercentRate } from '../value-objects/PercentRate';
import { AgeYears } from '../value-objects/AgeYears';

export class FireScenario {
  readonly id: string;
  readonly name: string;
  readonly monthlyExpenses: MoneyAmount;
  readonly currentPortfolio: MoneyAmount;
  readonly annualReturn: PercentRate;
  readonly inflation: PercentRate;
  readonly currentAge: AgeYears;
  readonly retirementAge: AgeYears;
  readonly monthlyContribution: MoneyAmount;
  readonly withdrawalTiming: WithdrawalTiming;
  readonly swr: PercentRate;
  readonly contributionGrowthEnabled: boolean;
  readonly events: readonly ScenarioEvent[];
  readonly phase: ScenarioPhase;
  readonly createdAt: Date;
  readonly updatedAt: Date;

  private constructor(init: {
    id: string;
    name: string;
    monthlyExpenses: MoneyAmount;
    currentPortfolio: MoneyAmount;
    annualReturn: PercentRate;
    inflation: PercentRate;
    currentAge: AgeYears;
    retirementAge: AgeYears;
    monthlyContribution: MoneyAmount;
    withdrawalTiming: WithdrawalTiming;
    swr: PercentRate;
    contributionGrowthEnabled: boolean;
    events: readonly ScenarioEvent[];
    phase: ScenarioPhase;
    createdAt: Date;
    updatedAt: Date;
  }) {
    this.id = init.id;
    this.name = init.name;
    this.monthlyExpenses = init.monthlyExpenses;
    this.currentPortfolio = init.currentPortfolio;
    this.annualReturn = init.annualReturn;
    this.inflation = init.inflation;
    this.currentAge = init.currentAge;
    this.retirementAge = init.retirementAge;
    this.monthlyContribution = init.monthlyContribution;
    this.withdrawalTiming = init.withdrawalTiming;
    this.swr = init.swr;
    this.contributionGrowthEnabled = init.contributionGrowthEnabled;
    this.events = init.events;
    this.phase = init.phase;
    this.createdAt = init.createdAt;
    this.updatedAt = init.updatedAt;
  }

  static create(init: FireScenarioInit): FireScenario {
    const now = new Date();
    return new FireScenario({
      id: init.id,
      name: init.name,
      monthlyExpenses: new MoneyAmount(init.monthlyExpenses),
      currentPortfolio: new MoneyAmount(init.currentPortfolio ?? 0),
      annualReturn: new PercentRate(init.annualReturn ?? 0.07),
      inflation: new PercentRate(init.inflation ?? 0.03),
      currentAge: new AgeYears(init.currentAge),
      retirementAge: new AgeYears(init.retirementAge),
      monthlyContribution: new MoneyAmount(init.monthlyContribution ?? 0),
      withdrawalTiming: init.withdrawalTiming ?? WithdrawalTiming.beginningOfYear,
      swr: new PercentRate(init.swr ?? 0.04),
      contributionGrowthEnabled: init.contributionGrowthEnabled ?? true,
      events: (init.events ?? []).map((e) =>
        e instanceof ScenarioEvent ? e : new ScenarioEvent(e),
      ),
      phase: init.phase ?? ScenarioPhase.mvp,
      createdAt: init.createdAt ?? now,
      updatedAt: init.updatedAt ?? now,
    });
  }

  get yearsToTarget(): number {
    return this.retirementAge.value - this.currentAge.value;
  }

  get monthsToTarget(): number {
    return this.yearsToTarget * 12;
  }

  copyWith(changes: Partial<FireScenarioInit>): FireScenario {
    return FireScenario.create({
      id: changes.id ?? this.id,
      name: changes.name ?? this.name,
      monthlyExpenses: changes.monthlyExpenses ?? this.monthlyExpenses.value,
      currentPortfolio:
        changes.currentPortfolio ?? this.currentPortfolio.value,
      annualReturn: changes.annualReturn ?? this.annualReturn.value,
      inflation: changes.inflation ?? this.inflation.value,
      currentAge: changes.currentAge ?? this.currentAge.value,
      retirementAge: changes.retirementAge ?? this.retirementAge.value,
      monthlyContribution:
        changes.monthlyContribution ?? this.monthlyContribution.value,
      withdrawalTiming: changes.withdrawalTiming ?? this.withdrawalTiming,
      swr: changes.swr ?? this.swr.value,
      contributionGrowthEnabled:
        changes.contributionGrowthEnabled ?? this.contributionGrowthEnabled,
      events: changes.events ?? this.events,
      phase: changes.phase ?? this.phase,
      createdAt: this.createdAt,
      updatedAt: changes.updatedAt ?? new Date(),
    });
  }

  equals(other: FireScenario): boolean {
    return (
      this.id === other.id &&
      this.monthlyExpenses.equals(other.monthlyExpenses) &&
      this.currentPortfolio.equals(other.currentPortfolio) &&
      this.annualReturn.equals(other.annualReturn) &&
      this.inflation.equals(other.inflation) &&
      this.currentAge.equals(other.currentAge) &&
      this.retirementAge.equals(other.retirementAge) &&
      this.monthlyContribution.equals(other.monthlyContribution) &&
      this.withdrawalTiming === other.withdrawalTiming &&
      this.swr.equals(other.swr) &&
      this.contributionGrowthEnabled === other.contributionGrowthEnabled
    );
  }
}

// Re-exports for downstream consumers that only import this module.
export {
  CalculationMode,
  FireMethod,
  WithdrawalTiming,
  ScenarioPhase,
};